import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, Skeleton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  WbSunny,
  Cloud,
  Grain,
  AcUnit,
  Thunderstorm,
  Opacity,
} from '@mui/icons-material';

interface CityWeather {
  city: string;
  temp: number;
  icon: string;
  windSpeed: number;
  description: string;
}

interface City {
  name: string;
  displayName: string;
  lat: number;
  lon: number;
  country: string;
}

// Paragliding locations ordered by importance in the Italian paragliding community
const CITIES: City[] = [
  // Most important Italian paragliding sites
  { name: 'Bassano', displayName: 'Bassano', lat: 45.7657, lon: 11.7272, country: 'IT' }, // Monte Grappa - most famous
  { name: 'Gemona', displayName: 'Gemona', lat: 46.2794, lon: 13.1336, country: 'IT' }, // Meduno area - major site
  { name: 'Feltre', displayName: 'Feltre', lat: 46.0183, lon: 11.9056, country: 'IT' }, // Feltre/Avena
  // Southern Switzerland - Ticino (very close to Italy)
  { name: 'Miglieglia', displayName: 'Miglieglia', lat: 46.0447, lon: 8.9889, country: 'CH' }, // Famous Ticino site
  { name: 'Locarno', displayName: 'Locarno', lat: 46.1699, lon: 8.7996, country: 'CH' }, // Cardada-Cimetta
  { name: 'Bellinzona', displayName: 'Bellinzona', lat: 46.1920, lon: 9.0215, country: 'CH' }, // Capital of Ticino
  // More Italian sites
  { name: 'Laveno', displayName: 'Laveno', lat: 45.9092, lon: 8.6247, country: 'IT' }, // Laveno Mombello - Lake Maggiore
  { name: 'Norma', displayName: 'Norma', lat: 41.5858, lon: 12.9681, country: 'IT' }, // Near Rome, popular site
  { name: 'Domodossola', displayName: 'Domodossola', lat: 46.1151, lon: 8.2926, country: 'IT' }, // Northern Alps
  { name: 'Aosta', displayName: 'Aosta', lat: 45.7372, lon: 7.3206, country: 'IT' }, // Valle d'Aosta
  // Famous Swiss sites
  { name: 'Interlaken', displayName: 'Interlaken', lat: 46.6863, lon: 7.8632, country: 'CH' }, // Most famous
  { name: 'Verbier', displayName: 'Verbier', lat: 46.0960, lon: 7.2285, country: 'CH' },
  { name: 'Lugano', displayName: 'Lugano', lat: 46.0037, lon: 8.9511, country: 'CH' }, // Monte Brè
  // Additional Italian sites
  { name: 'Castelluccio', displayName: 'Castelluccio', lat: 42.8277, lon: 13.2065, country: 'IT' }, // Umbria
  { name: 'Borso', displayName: 'Borso', lat: 45.8247, lon: 11.7869, country: 'IT' }, // Monte Grappa area
  { name: 'Monte Baldo', displayName: 'M.Baldo', lat: 45.7333, lon: 10.8667, country: 'IT' }, // Lake Garda
];

const WeatherCarousel: React.FC = () => {
  const [weatherData, setWeatherData] = useState<CityWeather[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollInterval = useRef<NodeJS.Timeout | null>(null);

  // Number of cities to show at once
  const citiesPerView = 3; // Show 3 cities

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchAllWeather = async () => {
      const apiKey = process.env.REACT_APP_OPENWEATHER_API_KEY;
      
      // If no API key, show demo data
      if (!apiKey || apiKey === 'demo') {
        const demoData: CityWeather[] = CITIES.map(city => ({
          city: city.displayName,
          temp: Math.floor(Math.random() * 15) + 10,
          icon: ['01d', '02d', '03d', '04d'][Math.floor(Math.random() * 4)],
          windSpeed: Math.floor(Math.random() * 20) + 5,
          description: 'Clear sky',
        }));
        setWeatherData(demoData);
        setLoading(false);
        return;
      }

      try {
        const promises = CITIES.map(async (city) => {
          const response = await fetch(
            `https://api.openweathermap.org/data/2.5/weather?lat=${city.lat}&lon=${city.lon}&appid=${apiKey}&units=metric`,
            { signal: abortController.signal }
          );
          
          if (response.ok) {
            const data = await response.json();
            return {
              city: city.displayName,
              temp: Math.round(data.main.temp),
              icon: data.weather[0].icon,
              windSpeed: Math.round(data.wind.speed * 3.6), // Convert m/s to km/h
              description: data.weather[0].description,
            };
          }
          return null;
        });

        const results = await Promise.all(promises);
        const validResults = results.filter((r): r is CityWeather => r !== null);
        setWeatherData(validResults);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Weather fetch error:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllWeather();
    // Refresh every 15 minutes
    const interval = setInterval(fetchAllWeather, 15 * 60 * 1000);

    return () => {
      abortController.abort();
      clearInterval(interval);
    };
  }, []);

  // Auto-scroll carousel
  useEffect(() => {
    if (weatherData.length <= citiesPerView) return;

    const startAutoScroll = () => {
      autoScrollInterval.current = setInterval(() => {
        setCurrentIndex(prev => {
          const maxIndex = weatherData.length - citiesPerView;
          return prev >= maxIndex ? 0 : prev + 1;
        });
      }, 8000); // Change every 8 seconds for slower switching
    };

    startAutoScroll();

    return () => {
      if (autoScrollInterval.current) {
        clearInterval(autoScrollInterval.current);
      }
    };
  }, [weatherData.length, citiesPerView]);

  const handlePrevious = () => {
    // Stop auto-scroll when user interacts
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
    }
    
    setCurrentIndex(prev => {
      const newIndex = prev - 1;
      return newIndex < 0 ? weatherData.length - citiesPerView : newIndex;
    });

    // Resume auto-scroll after 15 seconds
    setTimeout(() => {
      if (weatherData.length > citiesPerView) {
        autoScrollInterval.current = setInterval(() => {
          setCurrentIndex(prev => {
            const maxIndex = weatherData.length - citiesPerView;
            return prev >= maxIndex ? 0 : prev + 1;
          });
        }, 8000);
      }
    }, 15000);
  };

  const handleNext = () => {
    // Stop auto-scroll when user interacts
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
    }

    setCurrentIndex(prev => {
      const maxIndex = weatherData.length - citiesPerView;
      return prev >= maxIndex ? 0 : prev + 1;
    });

    // Resume auto-scroll after 15 seconds
    setTimeout(() => {
      if (weatherData.length > citiesPerView) {
        autoScrollInterval.current = setInterval(() => {
          setCurrentIndex(prev => {
            const maxIndex = weatherData.length - citiesPerView;
            return prev >= maxIndex ? 0 : prev + 1;
          });
        }, 8000);
      }
    }, 15000);
  };

  const getWeatherIcon = (iconCode: string) => {
    const iconSize = 16;
    const iconStyle = { fontSize: iconSize };
    
    const iconMap: { [key: string]: React.ReactElement } = {
      '01': <WbSunny sx={{ ...iconStyle, color: '#ffa726' }} />,
      '02': <WbSunny sx={{ ...iconStyle, color: '#ffb74d' }} />,
      '03': <Cloud sx={{ ...iconStyle, color: '#78909c' }} />,
      '04': <Cloud sx={{ ...iconStyle, color: '#546e7a' }} />,
      '09': <Grain sx={{ ...iconStyle, color: '#42a5f5' }} />,
      '10': <Grain sx={{ ...iconStyle, color: '#29b6f6' }} />,
      '11': <Thunderstorm sx={{ ...iconStyle, color: '#7e57c2' }} />,
      '13': <AcUnit sx={{ ...iconStyle, color: '#66bbff' }} />,
      '50': <Opacity sx={{ ...iconStyle, color: '#90a4ae' }} />,
    };

    const prefix = iconCode.substring(0, 2);
    return iconMap[prefix] || <WbSunny sx={iconStyle} />;
  };

  const getWindColor = (speed: number) => {
    if (speed < 10) return '#4caf50'; // Green - calm
    if (speed < 20) return '#ff9800'; // Orange - moderate
    if (speed < 30) return '#ff5722'; // Red-orange - strong
    return '#f44336'; // Red - very strong
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: { xs: 'none', lg: 'flex' }, 
        alignItems: 'center',
        justifyContent: 'center', 
        gap: 1,
      }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" width={140} height={24} sx={{ borderRadius: 0.5 }} />
        ))}
      </Box>
    );
  }

  if (weatherData.length === 0) return null;

  const visibleCities = weatherData.slice(currentIndex, currentIndex + citiesPerView);

  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'flex' },
        alignItems: 'center',
        gap: 0.5,
        position: 'relative',
        minWidth: 0, // Allow shrinking
        width: 'auto', // Dynamic width based on content
      }}
    >
      {weatherData.length > citiesPerView && (
        <IconButton
          size="small"
          onClick={handlePrevious}
          sx={{
            p: 0.25,
            color: '#999',
            '&:hover': {
              color: '#666',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: 16 }} />
        </IconButton>
      )}

      <Box
        ref={carouselRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          overflow: 'visible', // Allow content to be fully visible
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {visibleCities.map((city, index) => (
          <React.Fragment key={`${city.city}-${currentIndex}-${index}`}>
            {index > 0 && (
              <Typography
                variant="body2"
                sx={{
                  color: '#e0e0e0',
                  fontWeight: 300,
                  mx: 0.25,
                }}
              >
                |
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                transition: 'all 0.3s ease',
                cursor: 'default',
                flexShrink: 0, // Prevent shrinking
                animation: 'fadeIn 0.5s ease',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 },
                },
              }}
            >
              {getWeatherIcon(city.icon)}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: '#555',
                  fontSize: '0.7rem',
                  fontFamily: '"Inter", "Roboto", sans-serif',
                  whiteSpace: 'nowrap', // Prevent text wrapping
                  overflow: 'visible', // Show full text
                }}
              >
                {city.city}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: '#333',
                  fontSize: '0.75rem',
                }}
              >
                {city.temp}°
              </Typography>
              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: getWindColor(city.windSpeed),
                  ml: 0.25,
                  title: `${city.windSpeed} km/h`,
                }}
              />
            </Box>
          </React.Fragment>
        ))}
      </Box>

      {weatherData.length > citiesPerView && (
        <IconButton
          size="small"
          onClick={handleNext}
          sx={{
            p: 0.25,
            color: '#999',
            '&:hover': {
              color: '#666',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <ChevronRight sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default WeatherCarousel;